import { useEffect } from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useAppState } from "../state/AppState";
import { useLang } from "../i18n/LangContext";
import { SearchField } from "./SearchField";
import { SpotList } from "./SpotList";

interface SpotSearchSheetProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}

/** Mobile-only fullscreen bottom-sheet: tap the search FAB → drawer
 *  slides up with an auto-focused search field on top and the live-
 *  filtered spot list below. Tapping a spot navigates to /spot/:id,
 *  which we observe via `selectedId` and auto-close the sheet so the
 *  user lands on the map view with the detail panel open. */
export function SpotSearchSheet({ open, onOpen, onClose }: SpotSearchSheetProps) {
  const { t } = useLang();
  const { selectedId } = useAppState();

  useEffect(() => {
    if (open && selectedId) onClose();
  }, [open, selectedId, onClose]);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      disableSwipeToOpen
      swipeAreaWidth={0}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          bgcolor: "background.paper",
          backgroundImage: "none",
          borderRadius: "18px 18px 0 0",
          height: "92dvh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <div className="spot-sheet__head">
        <div className="spot-sheet__handle" aria-hidden="true" />
        <div className="spot-sheet__search-row">
          <div className="spot-sheet__search">
            <SearchField autoFocus />
          </div>
          <IconButton
            aria-label={t("close")}
            onClick={onClose}
            size="small"
            sx={{ color: "text.primary", flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </div>
      <div className="spot-sheet__body">
        <SpotList />
      </div>
    </SwipeableDrawer>
  );
}
